import { Cta } from "../../components/Cta";
import { gstIncludedInCents, gstRegistered } from "../../content/legal";
import { useFocusTrap } from "../../lib/useFocusTrap";
import styles from "./UpgradeDialog.module.css";

interface PreviewLine {
  description: string;
  amountCents: number;
}

interface Props {
  /** The plan being moved to. Named in the heading, so it is never ambiguous. */
  planName: string;
  /** Stripe's figure, or null while it is still being fetched. */
  amountCents: number | null;
  /**
   * Set only for a move DOWN, and already formatted for a person. Its presence
   * is what switches this dialog from "here is what you are charged today" to
   * "here is when it changes" — the amount is genuinely zero in that case, and a
   * zero rendered as a charge reads as a free upgrade.
   */
  scheduledFor?: string;
  currency: string;
  /** Stripe's own proration lines: the credit for what is being left, and the
      charge for what is being joined. Empty from an older server. */
  lines: PreviewLine[];
  /** Tax Stripe actually calculated, or null when it calculated none. */
  taxCents: number | null;
  busy: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

/** `−$67.99` / `$129.96`. A true minus sign, because a credit line that reads as a
    hyphen at small sizes is the one number here nobody may misread. */
function money(cents: number): string {
  return `${cents < 0 ? "−" : ""}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

/**
 * The upgrade confirmation, in front of the page and itemised.
 *
 * ── WHY IT IS A DIALOG AND NOT A PANEL (Ned, 2026-08-29) ────────────────────
 *
 * It was a `<Card>` rendered after the plan grid, so on any viewport the cards
 * fill it sat BELOW THE FOLD. Clicking "Upgrade to Pro" looked like it did
 * nothing: the confirmation, the amount and both buttons were correct and
 * off-screen. Reported as "it isn't redirecting to Stripe" — which it never does,
 * because an upgrade charges the card already on file, so the missing feedback
 * was the whole of the problem. A dialog cannot be scrolled past.
 *
 * ── WHY IT IS ITEMISED ──────────────────────────────────────────────────────
 *
 * "You'll be charged $61.97" is a number the customer cannot check. A proration
 * is two facts — a credit for the unused part of the plan being left and a charge
 * for the remainder of the one being joined — and without them the total looks
 * arbitrary on a screen that is about to take money. Every line comes from
 * Stripe's upcoming invoice, in Stripe's own wording, so what is shown here and
 * what appears on the invoice cannot disagree.
 *
 * ── AND WHY THERE MAY BE NO GST LINE ────────────────────────────────────────
 *
 * Two sources, in order. If Stripe Tax is configured the invoice carries a real
 * tax figure and that is authoritative. Otherwise, prices are advertised
 * GST-inclusive, so the tax is a component of a total already agreed — and
 * `gstRegistered()` decides whether we may say so at all. It keys on the ABN,
 * which is still a placeholder: stating a GST component while unregistered is a
 * misrepresentation, and `legal.ts` refused to compute an ABN from the ACN for
 * exactly this reason. Until it is filled in, the dialog states the total and
 * says nothing about tax.
 */
export function UpgradeDialog({
  planName,
  amountCents,
  scheduledFor,
  currency,
  lines,
  taxCents,
  busy,
  onConfirm,
  onDismiss,
}: Props) {
  /* Always an overlay, so `active` is unconditional — the second condition
     ChatShell's drawers pass exists for things that are only sometimes modal. */
  const ref = useFocusTrap(true, onDismiss);
  const priced = amountCents !== null;

  /**
   * `busy` means two different things, and conflating them is a real bug.
   *
   * `useUpgrade` raises it for BOTH the price lookup and the charge, because it
   * is one hook driving one button. In here that distinction is the difference
   * between "hold on" and "your card is being charged": before the amount lands,
   * busy is a PRICE LOOKUP; after, it is the upgrade itself.
   *
   * Both mistakes were caught by the first run of Pricing.upgradeDialog.test.tsx
   * and both were worse than cosmetic. The confirm button read "Upgrading…" while
   * merely fetching a figure — telling someone their card was being charged when
   * nothing had been committed — and "Not now" was disabled at the same time, so
   * the one moment they most wanted out was the moment they could not leave.
   */
  const committing = busy && priced;

  /* Stripe's tax if it computed one; otherwise the GST inside a GST-inclusive
     total, and only if we are registered to say so. */
  const inclusiveGst =
    taxCents === null && priced && gstRegistered() ? gstIncludedInCents(amountCents) : null;

  return (
    <>
      {/* A button, not a div, so the scrim is reachable and announced rather than
          being a click target only a mouse can find. Dismissing is the SAFE
          direction here — nothing has been charged — which is why a stray click
          on the scrim is allowed to close it at all. */}
      <button
        type="button"
        className={styles.scrim}
        /* NOT "Not now" — that is the dismiss button's name, and two controls
           sharing one accessible name is ambiguous to a screen reader and to
           `getByRole`. The test found both and could not say which it meant. */
        aria-label="Close"
        onClick={onDismiss}
        disabled={committing}
      />
      <div
        ref={ref}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-heading"
      >
        <h2 id="upgrade-heading" className={styles.heading}>
          {scheduledFor ? `Switching to ${planName}` : `Upgrading to ${planName}`}
        </h2>

        {/* Announced when it changes, because the interesting content arrives
            after the dialog opens — a screen reader user would otherwise be told
            only that it is loading, and never what the charge turned out to be. */}
        <div aria-live="polite">
          {scheduledFor ? (
            /* No <dl> of proration, because there is none: they keep what they
               are on for every day already paid for, and the next invoice is
               simply the cheaper one. Stating a total here — even zero — would
               invite the reading that this is free. */
            <p className={styles.body}>
              You&rsquo;ll stay on your current plan until{" "}
              <strong>{scheduledFor}</strong>, then move to {planName}. Nothing to
              pay today, and you keep everything you&rsquo;ve paid for until then.
            </p>
          ) : priced ? (
            <dl className={styles.items}>
              {lines.map((line, i) => (
                <div className={styles.item} key={`${line.description}-${i}`}>
                  <dt className={styles.itemLabel}>{line.description}</dt>
                  <dd className={styles.itemAmount}>{money(line.amountCents)}</dd>
                </div>
              ))}

              {taxCents !== null && (
                <div className={styles.item}>
                  <dt className={styles.itemLabel}>GST</dt>
                  <dd className={styles.itemAmount}>{money(taxCents)}</dd>
                </div>
              )}

              {/* The total is Stripe's `amount_due`, never a sum of the lines
                  above — rounding can put those a cent apart, and the invoice is
                  the one that charges. */}
              <div className={`${styles.item} ${styles.totalRow}`}>
                <dt className={styles.totalLabel}>Charged today</dt>
                <dd className={styles.amount}>
                  {money(amountCents)} {currency}
                </dd>
              </div>

              {inclusiveGst !== null && (
                <div className={styles.item}>
                  <dt className={styles.gstNote}>Includes GST</dt>
                  <dd className={styles.gstNote}>{money(inclusiveGst)}</dd>
                </div>
              )}
            </dl>
          ) : (
            /* The figures come from Stripe's upcoming invoice, never from
               arithmetic over the price table here — those can disagree, and the
               invoice is the one that charges. So there is a wait, and it is
               named. */
            <p className={styles.body}>Working out what you&rsquo;ll be charged today…</p>
          )}
        </div>

        <p className={styles.footnote}>
          {scheduledFor
            ? "You're not cancelling. The subscription continues on the new plan, " +
              "so there's nothing to set up again afterwards."
            : "That's the new plan for the rest of this month, less the unused part of " +
              "what you're on now. Your existing subscription changes over rather than " +
              "running alongside, so you're never paying for two."}
        </p>

        <div className={styles.action}>
          {/* Disabled until the amount is known. Confirming an unstated charge is
              the thing this dialog exists to prevent. */}
          {/* A scheduled change has no amount to wait for, so it is not gated on
              `priced` — that gate exists to stop someone confirming an unstated
              CHARGE, and there is no charge here. */}
          <Cta disabled={busy || (!priced && !scheduledFor)} onClick={onConfirm}>
            {scheduledFor
              ? (committing ? "Scheduling…" : "Confirm switch")
              : (committing ? "Upgrading…" : "Confirm upgrade")}
          </Cta>
          {/* Enabled while the price is being fetched. Backing out during the
              wait must always be possible — nothing has been committed yet, and
              a dialog someone cannot leave is worse than no dialog. */}
          <button
            type="button"
            className={styles.dismiss}
            onClick={onDismiss}
            disabled={committing}
          >
            Not now
          </button>
        </div>
      </div>
    </>
  );
}
